/* eslint-disable @typescript-eslint/no-explicit-any */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Field, Combobox, Option, Skeleton, SkeletonItem } from '@fluentui/react-components';
import { useWorkItemFieldHook } from './use-work-item-field';
import { useAzureProvider } from 'src/azure/context';
import { ActionItemType, EditWorkItemActionType } from '../../types';
import { useAutomationRuleActions } from '../../use-actions';
import { editWorkItemStyles } from '../styles';

export interface Props {
  actionId: string;
  validationError: string | null;
	initialOptions?: Array<{ label: string; value: string }>;
	onChangeWorkItemField(fieldName: string): void;
}

export default function WorkItemFieldInput(props: Props) {
	const classes = editWorkItemStyles();
	const {applicationContext} = useAzureProvider();
	const {actions, setActions, resetValidationErrors} = useAutomationRuleActions();

	const workItemFieldHook = useWorkItemFieldHook();

	const [onInitialLoading, setOnInitialLoading] = useState(false);
	const [options, setOptions] = useState<Array<{ value: string; label: string }>>([]);
	
	const [status, setStatus] = useState<'searching' | 'selected'>('selected');
	const [text, setText] = useState<string>('');

	const current: EditWorkItemActionType = useMemo(() => {
		const foundActions = actions.find(item => item.id === props.actionId) as ActionItemType;
		return foundActions!.editWorkItem as EditWorkItemActionType;
	}, [actions, props.actionId]);
  
	const onChangeText = useCallback((value: string) => {
		setStatus('searching');
		setText(value);
	
		if (value === '') {
			workItemFieldHook
				.getWorkItemFieldOptions({
					projectId: applicationContext.projectId,
				})
				.then((resultOptions) => {
					setOptions(resultOptions);
				});
		}
		
		if (value !== '') {
			const filteredOptions = options.filter(item => item.label.toLowerCase().includes(value.toLowerCase()));
			setOptions(filteredOptions);
		}
	}, [options, applicationContext.projectId]);

	const setWorkItemField = useCallback((value: string) => {
		resetValidationErrors();
		if (value) {
			setStatus('selected');
			const foundAction = actions.find(item => item.id === props.actionId);
	
			if (foundAction) {
				props.onChangeWorkItemField(value);
				setActions(oldActions => {
					const current = oldActions.find(item => item.id === props.actionId) as ActionItemType;
					const others = oldActions.filter(item => item.id !== props.actionId);
	
					return [...others, {
						...current, editWorkItem: {
							...current.editWorkItem as EditWorkItemActionType,
							workItemField: value,
							userFieldValue: [],
							dateTimeValue: {
								type: 'datetime',
								datetime: {
									value: new Date(),
								},
								timeframe: {
									timeNumber: 0,
									timeUnit: 'minutes',
									timeOption: 'ago',
								},
							}
						}
					}];
				});
			}
		}
	}, []);

	const loadOptions = useCallback(async () => {
		const resultOptions = await workItemFieldHook.getWorkItemFieldOptions({projectId: applicationContext.projectId});
		setOptions(resultOptions);
	}, []); 

	const valueRender = useMemo(() => {
		if (current && current.workItemField && status === 'selected') {
			return options.find(item => item.value === current.workItemField)?.label || '';
		}

		if (text) {
			return text;
		}

		return '';
	}, [text, current.workItemField, options, status]);

	const selectedOptions = useMemo(() => {
		const found = options.find(item => item.value === current.workItemField);

		if (current.workItemField && found) {
			return [found.value];
		}

		return [];
	}, [current.workItemField, options]);

	useEffect(() => {
		setOnInitialLoading(true);
		const foundAction = actions.find(item => item.id === props.actionId);

		if (
			foundAction && 
			foundAction?.editWorkItem &&
			props.initialOptions
		) {
			// loadOptions();
			setOptions(props.initialOptions);
		}
		setOnInitialLoading(false);
	}, []);

	if (onInitialLoading) {
		return (
			<Skeleton className={classes.skeletonRoot}>
				<SkeletonItem className={classes.skeletonItem} />
			</Skeleton>
		);
	}

	return (
		<Field
			className={classes.dropdownRoot}
			validationMessage={props.validationError}
		>
			<Combobox
				id={`action-${props.actionId}-work-item-field`}
				className={classes.dropdown}
				autoComplete='off'
				appearance='outline'
				placeholder='Choose a field'
				positioning='below-start'
				clearable
				value={valueRender}
				selectedOptions={selectedOptions}
				onChange={(ev) => {
					onChangeText(ev.target.value);
				}}
				onOptionSelect={(_, data) => {
					setWorkItemField(data.optionValue as string);
				}}
				onBlur={() => {
					if (props.initialOptions) {
						setOptions(props.initialOptions);
						setStatus('selected');
					}

					if (!props.initialOptions) {
						loadOptions()
							.then(() => {
								setStatus('selected');
							});
					}
				}}
			>
				<div style={{maxHeight: 240}}>
					{options.map((item, idx) => (
						<Option key={idx} value={item.value}>{item.label}</Option>
					))}
				</div>
			</Combobox>
		</Field>
	);
}